import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LayoutDashboard, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const { t, lang, setLang, languages } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const LangPills = ({ compact }) => (
    <div className="flex items-center gap-1" data-testid="lang-switcher">
      {languages.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          title={l.label}
          className={`flex items-center gap-1 rounded-full font-semibold transition-colors ${compact ? "px-1.5 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs"} ${l.code === lang ? "adj-gradient text-white shadow" : "text-slate-300 hover:text-white hover:bg-white/10"}`}
          data-testid={`lang-option-${l.code}`}
        >
          <span className="text-sm leading-none">{l.flag}</span>
          {!compact && <span>{l.short}</span>}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src="/adj-logo.png" alt="ADJ Academia" className="h-9 w-9 object-contain group-hover:scale-110 transition-transform" />
          <div className="leading-none">
            <span className="font-display font-extrabold text-lg tracking-tight text-white">ADJ</span>
            <span className="font-mono text-[10px] block tracking-[0.3em] text-[#F5842B]">ACADEMIA</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-home">{t("nav.home")}</Link>
          <a href="/#corsi" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-courses">{t("nav.courses")}</a>
          <a href="/#bundle" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-bundle">{t("nav.bundle")}</a>
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <LangPills />
          {user ? (
            <>
              <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-slate-200 hover:text-white transition-colors" data-testid="nav-dashboard">
                <LayoutDashboard size={16} /> {t("nav.dashboard")}
              </button>
              {user.is_admin && (
                <button onClick={() => navigate("/admin")} className="text-sm font-semibold text-[#F5842B] hover:text-white transition-colors" data-testid="nav-admin">
                  {t("admin.nav")}
                </button>
              )}
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full border border-white/10 object-cover" />
              ) : (
                <div className="h-9 w-9 rounded-full adj-gradient flex items-center justify-center text-sm font-bold text-white">{user.name?.[0] || "U"}</div>
              )}
              <button onClick={logout} className="text-slate-400 hover:text-[#E23B2E] transition-colors" data-testid="nav-logout" title={t("nav.logout")}>
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <button onClick={login} className="adj-gradient text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity" data-testid="nav-login">
              {t("nav.login")}
            </button>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LangPills compact />
          <button className="text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-white/10 px-5 py-5 flex flex-col gap-4" data-testid="nav-mobile-menu">
          <Link to="/" onClick={() => setOpen(false)} className="text-slate-200">{t("nav.home")}</Link>
          <a href="/#corsi" onClick={() => setOpen(false)} className="text-slate-200">{t("nav.courses")}</a>
          <a href="/#bundle" onClick={() => setOpen(false)} className="text-slate-200">{t("nav.bundle")}</a>
          {user ? (
            <>
              <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="text-left text-slate-200">{t("nav.dashboard")}</button>
              {user.is_admin && (
                <button onClick={() => { setOpen(false); navigate("/admin"); }} className="text-left font-semibold text-[#F5842B]">{t("admin.nav")}</button>
              )}
              <button onClick={logout} className="text-left text-[#E23B2E]">{t("nav.logout")}</button>
            </>
          ) : (
            <button onClick={login} className="adj-gradient text-white font-semibold px-5 py-3 rounded-full">{t("nav.login")}</button>
          )}
        </div>
      )}
    </header>
  );
}
